<template>
  <div class="address-search-container">
    <!-- 固定ヘッダー部分 -->
    <div class="search-header">
      <div class="autocomplete-wrapper">
        <v-text-field
            variant="outlined"
            density="compact"
            label="住所、郵便番号、座標で検索"
            prepend-inner-icon="mdi-magnify"
            v-model="address"
            @input="onAddressInput"
            @keydown.enter="onEnterKey"
            @keydown.down.prevent="onArrowDown"
            @keydown.up.prevent="onArrowUp"
            @keydown.escape="closeSuggestions"
            @blur="onBlur"
            @compositionstart="isComposing = true"
            @compositionend="isComposing = false"
            class="search-field"
            hide-details
            clearable
            autocomplete="off"
        >
          <template v-slot:append>
            <v-btn
                icon="mdi-magnify"
                size="small"
                color="primary"
                variant="flat"
                @click="sercheAdress"
                :disabled="!address"
            ></v-btn>
          </template>
        </v-text-field>

        <!-- サジェストリスト -->
        <div v-if="showSuggestions && suggestions.length > 0" class="suggestions-list">
          <div
              v-for="(suggestion, index) in suggestions"
              :key="index"
              class="suggestion-item"
              :class="{ 'suggestion-item--active': index === selectedIndex }"
              @mousedown.prevent="selectSuggestion(suggestion)"
              @mouseover="selectedIndex = index"
          >
            <v-icon size="16" class="mr-2" color="grey">mdi-map-marker</v-icon>
            <span>{{ suggestion.properties.title }}</span>
          </div>
        </div>

        <!-- ローディング表示（サジェスト検索中 or AI/GSI検索中） -->
        <div v-if="isLoading || isSearching" class="suggestions-loading">
          <v-progress-circular size="16" width="2" indeterminate color="primary" class="mr-2"></v-progress-circular>
          <span>検索中...</span>
        </div>
      </div>

      <!-- AI検索ヒント（一時的にコメントアウト）
      <div class="ai-hint">
        <v-icon size="14" color="green" class="mr-1">mdi-robot</v-icon>
        <span>自然語OK（例：「近くのコンビニ」「東京駅」）</span>
      </div>
      -->

      <!-- 座標系オプション（折りたたみ式） -->
      <div class="coord-options">
        <div class="coord-toggle" @click="showCoordOptions = !showCoordOptions">
          <v-icon size="14" class="mr-1">{{ showCoordOptions ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
          <span>座標系オプション</span>
          <span v-if="zahyokeiForSercheAdress !== 'WGS84'" class="coord-badge">{{ zahyokeiForSercheAdress }}</span>
        </div>
        <div v-show="showCoordOptions" class="coord-select-wrapper">
          <template v-if="s_isAndroid">
            <select v-model="zahyokeiForSercheAdress" class="native-select native-select-small">
              <option v-for="item in items" :key="item" :value="item">{{ item }}</option>
            </select>
          </template>
          <v-select v-else
                    v-model="zahyokeiForSercheAdress"
                    :items="items"
                    variant="outlined"
                    density="compact"
                    hide-details
          ></v-select>
        </div>
      </div>

      <!-- マーカー操作バー -->
      <div v-if="markers.length > 0" class="marker-actions">
        <span class="marker-count">
          <v-icon size="14" class="mr-1">mdi-map-marker</v-icon>
          {{ markers.length }}件
        </span>
        <v-btn size="small" variant="tonal" color="primary" @click="exportMarkersCSV">
          <v-icon size="16" class="mr-1">mdi-download</v-icon>CSV書き出し
        </v-btn>
        <v-btn size="small" variant="tonal" color="error" @click="clearAllMarkers">
          <v-icon size="16" class="mr-1">mdi-delete-sweep</v-icon>全削除
        </v-btn>
      </div>

      <!-- タブヘッダー（ログイン時のみ） -->
      <v-tabs v-if="isLoggedIn" v-model="activeTab" density="compact" grow class="tabs-header">
        <v-tab value="history">
          <v-icon size="16" class="mr-1">mdi-history</v-icon>
          履歴
          <v-badge v-if="searchHistory.length" :content="searchHistory.length" inline color="primary" class="ml-1"></v-badge>
        </v-tab>
        <v-tab value="favorites">
          <v-icon size="16" class="mr-1">mdi-star</v-icon>
          お気に入り
          <v-badge v-if="favoriteLocations.length" :content="favoriteLocations.length" inline color="primary" class="ml-1"></v-badge>
        </v-tab>
      </v-tabs>
    </div>

    <!-- スクロール可能なコンテンツ部分 -->
    <div class="scroll-content" v-if="isLoggedIn">
      <v-window v-model="activeTab">
        <!-- 履歴タブ -->
        <v-window-item value="history">
          <div class="tab-panel">
            <div v-if="searchHistory.length" class="history-list">
              <div
                  v-for="item in searchHistory"
                  :key="getHistoryTerm(item)"
                  class="history-item"
                  @click="selectHistory(item)"
              >
                <v-icon size="16" class="history-icon">mdi-clock-outline</v-icon>
                <span class="history-text">{{ getHistoryTerm(item) }}</span>
                <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    @click.stop="removeHistory(getHistoryTerm(item))"
                    class="action-btn action-btn--delete"
                >
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
            <div v-else class="empty-state">
              <v-icon size="40" color="grey-lighten-1">mdi-history</v-icon>
              <p>検索履歴がありません</p>
              <span class="empty-hint">住所や座標を検索すると履歴が保存されます</span>
            </div>
          </div>
        </v-window-item>

        <!-- お気に入りタブ -->
        <v-window-item value="favorites">
          <div class="tab-panel">
            <div class="panel-header">
              <v-btn
                  size="small"
                  variant="tonal"
                  color="primary"
                  @click="addCurrentLocationToFavorite"
                  class="add-favorite-btn"
              >
                <v-icon size="16" class="mr-1">mdi-plus</v-icon>
                現在地を追加
              </v-btn>
            </div>
            <div v-if="favoriteLocations.length" class="favorite-list">
              <div
                  v-for="fav in favoriteLocations"
                  :key="fav.name"
                  class="favorite-item"
                  @click="goToFavorite(fav)"
              >
                <div class="favorite-info">
                  <v-icon size="16" class="favorite-icon">mdi-star</v-icon>
                  <span class="favorite-name">{{ fav.name }}</span>
                </div>
                <div class="favorite-actions">
                  <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      @click.stop="editFavorite(fav)"
                      class="action-btn"
                  >
                    <v-icon size="16">mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      @click.stop="removeFavorite(fav.name)"
                      class="action-btn action-btn--delete"
                  >
                    <v-icon size="16">mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <v-icon size="40" color="grey-lighten-1">mdi-star-outline</v-icon>
              <p>お気に入り地点がありません</p>
              <span class="empty-hint">「現在地を追加」でよく使う場所を登録しましょう</span>
            </div>
          </div>
        </v-window-item>
      </v-window>
    </div>

    <!-- 非ログイン時 -->
    <div class="login-prompt" v-else>
      <v-icon size="20" color="grey" class="mr-2">mdi-account-outline</v-icon>
      <span>ログインすると検索履歴・お気に入りを保存できます</span>
    </div>

    <!-- お気に入り追加/編集ダイアログ -->
    <v-dialog v-model="showFavoriteDialog" max-width="360">
      <v-card class="favorite-dialog">
        <v-card-title class="dialog-title">
          <v-icon size="20" color="primary" class="mr-2">mdi-star</v-icon>
          {{ editingFavorite ? 'お気に入りを編集' : 'お気に入りに追加' }}
        </v-card-title>
        <v-card-text>
          <v-text-field
              v-model="favoriteName"
              label="地点名"
              variant="outlined"
              density="compact"
              hide-details
              autofocus
              placeholder="例: 自宅、会社、現場A"
              @keydown.enter="onFavoriteEnter"
          ></v-text-field>
          <div class="coords-display">
            <v-icon size="14" class="mr-1">mdi-crosshairs-gps</v-icon>
            {{ favoriteCoords.lat.toFixed(6) }}, {{ favoriteCoords.lng.toFixed(6) }}
            <span class="zoom-badge">Z{{ favoriteCoords.zoom }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeFavoriteDialog">キャンセル</v-btn>
          <v-btn color="primary" variant="flat" @click="saveFavorite" :disabled="!favoriteName.trim()">
            保存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- エラーダイアログ -->
    <v-dialog v-model="showErrorDialog" max-width="340">
      <v-card class="error-dialog">
        <v-card-text class="error-content">
          <div class="error-icon-wrapper">
            <v-icon size="48" color="orange-darken-2">mdi-map-marker-question</v-icon>
          </div>
          <p class="error-message">{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions class="error-actions">
          <v-btn color="primary" variant="flat" block @click="showErrorDialog = false">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 検索結果ダイアログ -->
    <v-dialog v-model="showResultsDialog" max-width="420">
      <v-card class="results-dialog">
        <v-card-title class="results-title">
          <v-icon size="20" color="primary" class="mr-2">mdi-map-marker-multiple</v-icon>
          <template v-if="areaPoiInfo">
            {{ areaPoiInfo.areaName }}の{{ areaPoiInfo.poiQuery }}（{{ areaPoiInfo.count }}件）
          </template>
          <template v-else>検索結果</template>
        </v-card-title>
        <v-card-text class="results-content">
          <div class="results-list" :style="areaPoiInfo ? 'max-height: 400px; overflow-y: auto' : ''">
            <div
                v-for="(result, index) in searchResults"
                :key="index"
                class="result-item"
                @click="selectSearchResult(result)"
            >
              <div v-if="result.source === 'nearby' || result.source === 'area_poi'" class="result-source" :class="getSourceClass(result.source)">
                {{ getSourceLabel(result.source) }}
              </div>
              <div class="result-info">
                <div class="result-title">{{ result.properties.title }}</div>
              </div>
              <v-icon size="18" color="grey">mdi-chevron-right</v-icon>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeResultsDialog">閉じる</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 確認ダイアログ -->
    <VDialogConfirm
      v-model="confirmDialog.show"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :color="confirmDialog.color"
      :icon="confirmDialog.icon"
      :icon-color="confirmDialog.iconColor"
      @ok="confirmDialog.resolve(true)"
      @cancel="confirmDialog.resolve(false)"
    />
  </div>
</template>

<script>
import {jgd2000ZoneToWgs84, fetchElevation, LngLatToAddress} from "@/js/downLoad";
import axios from "axios"
import maplibregl from 'maplibre-gl'
import proj4 from 'proj4'
import {mapState} from "vuex";
import VDialogConfirm from '@/components/V-dialog/V-dialog-confirm.vue'
import { consumeToken } from '@/js/tokenService'

// 座標系定義
const zahyokeiList = [
  { kei: '公共座標1系', code: 'EPSG:6669' },
  { kei: '公共座標2系', code: 'EPSG:6670' },
  { kei: '公共座標3系', code: 'EPSG:6671' },
  { kei: '公共座標4系', code: 'EPSG:6672' },
  { kei: '公共座標5系', code: 'EPSG:6673' },
  { kei: '公共座標6系', code: 'EPSG:6674' },
  { kei: '公共座標7系', code: 'EPSG:6675' },
  { kei: '公共座標8系', code: 'EPSG:6676' },
  { kei: '公共座標9系', code: 'EPSG:6677' },
  { kei: '公共座標10系', code: 'EPSG:6678' },
  { kei: '公共座標11系', code: 'EPSG:6679' },
  { kei: '公共座標12系', code: 'EPSG:6680' },
  { kei: '公共座標13系', code: 'EPSG:6681' },
  { kei: '公共座標14系', code: 'EPSG:6682' },
  { kei: '公共座標15系', code: 'EPSG:6683' },
  { kei: '公共座標16系', code: 'EPSG:6684' },
  { kei: '公共座標17系', code: 'EPSG:6685' },
  { kei: '公共座標18系', code: 'EPSG:6686' },
  { kei: '公共座標19系', code: 'EPSG:6687' },
];

export default {
  name: 'AddressSearch',
  components: { VDialogConfirm },
  data: () => ({
    address: '',
    zahyokeiForSercheAdress: 'WGS84',
    items: [
      'WGS84',
      '公共座標1系', '公共座標2系', '公共座標3系',
      '公共座標4系', '公共座標5系', '公共座標6系',
      '公共座標7系', '公共座標8系', '公共座標9系',
      '公共座標10系', '公共座標11系', '公共座標12系',
      '公共座標13系', '公共座標14系', '公共座標15系',
      '公共座標16系', '公共座標17系', '公共座標18系',
      '公共座標19系'
    ],
    // 座標系オプション表示
    showCoordOptions: false,
    // オートコンプリート用
    suggestions: [],
    showSuggestions: false,
    selectedIndex: -1, // マウスオーバー用（ハイライト表示）
    keyboardSelectedIndex: -1, // キーボード選択用（Enter確定）
    isLoading: false,
    debounceTimer: null,
    isComposing: false, // IME変換中フラグ
    // タブ
    activeTab: 'history',
    // お気に入りダイアログ用
    showFavoriteDialog: false,
    favoriteName: '',
    favoriteCoords: { lat: 0, lng: 0, zoom: 14 },
    editingFavorite: null,
    // エラーダイアログ用
    showErrorDialog: false,
    errorMessage: '',
    // 検索結果ダイアログ用
    showResultsDialog: false,
    searchResults: [],
    searchRecordTerm: '',
    isSearching: false, // 検索中フラグ
    areaPoiInfo: null, // エリアPOI検索情報 { areaName, poiQuery, count }
    // 確認ダイアログ用
    confirmDialog: {
      show: false,
      title: '',
      message: '',
      color: 'primary',
      icon: null,
      iconColor: 'primary',
      resolve: null,
    },
    // マーカー管理用
    markers: [], // { marker, lngLat: [lng, lat], markerId }
  }),
  computed: {
    ...mapState([
      'map01',
      'searchHistory',
      'favoriteLocations',
    ]),
    s_isAndroid () {
      return this.$store.state.isAndroid
    },
    isLoggedIn () {
      const uid = this.$store.state.userId || localStorage.getItem('lastUserId')
      return !!uid && uid !== 'guest'
    },
  },
  async mounted() {
    if (this.isLoggedIn) {
      this.$store.dispatch('loadSearchHistory')
      this.$store.dispatch('loadFavoriteLocations')
      // 保存済みマーカーを読み込み
      await this.$store.dispatch('loadSavedMarkers')
      // マップ準備完了後にマーカーを表示
      this.restoreSavedMarkers()
    }
  },
  beforeUnmount() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    this.clearAreaPoiMarkers()
  },
  methods: {
    // 小画面時に住所検索FWを閉じる
    closeWindowOnSmall() {
      if (window.innerWidth < 500) {
        this.$store.dispatch('hideFloatingWindow', 'address-search')
      }
    },

    // 全マーカーを削除
    async clearAllMarkers() {
      if (this.markers.length === 0) return

      const confirmed = await this.showConfirm({
        title: '全マーカー削除',
        message: `${this.markers.length}件のマーカーをすべて削除しますか？`,
        color: 'error',
        icon: 'mdi-delete-sweep',
        iconColor: 'error',
      })
      if (!confirmed) return

      // 地図上のマーカーを削除
      this.markers.forEach(m => m.marker.remove())
      // サーバーからも削除
      for (const m of this.markers) {
        if (m.markerId) {
          await this.$store.dispatch('removeSavedMarker', m.markerId)
        }
      }
      // 配列をクリア
      this.markers = []
    },

    // マーカーをCSVに書き出し
    exportMarkersCSV() {
      const savedMarkers = this.$store.state.savedMarkers || []
      const rows = this.markers.map(m => {
        const saved = savedMarkers.find(s => s.id === m.markerId)
        const title = (saved?.title || '検索地点').replace(/"/g, '""')
        const lat = m.lngLat[1]
        const lng = m.lngLat[0]
        return `"${title}",${lat},${lng}`
      })

      const csv = '\uFEFF' + '名称,緯度,経度\n' + rows.join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const now = new Date()
      const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
      link.href = URL.createObjectURL(blob)
      link.download = `markers_${dateStr}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    // 入力中はサジェストを出さない（Enter or 検索ボタンで検索）
    onAddressInput() {
      this.suggestions = []
      this.showSuggestions = false
    },

    isCoordinateFormat(input) {
      const coordRegex = /^\s*\[?\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\]?\s*$/
      return coordRegex.test(input)
    },

    // 郵便番号形式かチェック（123-4567 または 1234567 または 〒123-4567）
    // 後ろに住所が続いていてもOK（例: 〒880-0943 宮崎県...）
    isPostalCodeFormat(input) {
      return /^\s*〒?\s*(\d{3}-?\d{4})/.test(input)
    },

    // 郵便番号を抽出（後ろに住所があっても郵便番号部分だけ取得）
    extractPostalCode(input) {
      const match = input.match(/^\s*〒?\s*(\d{3}-?\d{4})/)
      return match ? match[1] : null
    },

    // 郵便番号から住所を取得
    async searchByPostalCode(postalCode) {
      // 〒、ハイフン、空白を除去して7桁に
      const code = postalCode.replace(/[〒\-\s]/g, '')
      try {
        // zipcloud APIを使用（無料・制限なし）
        const response = await axios.get(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${code}`)
        // statusは数値200または文字列"200"の場合がある
        if ((response.data.status === 200 || response.data.status === '200') && response.data.results && response.data.results.length > 0) {
          const result = response.data.results[0]
          // 都道府県 + 市区町村 + 町域
          return `${result.address1}${result.address2}${result.address3}`
        }
        return null
      } catch (error) {
        console.warn('郵便番号検索エラー:', error)
        return null
      }
    },

    // 地番形式を住所表示形式に正規化
    normalizeChiban(input) {
      // 「番地」「番地の」「番」などを正規化
      // 例: "東京都千代田区霞が関1234番地" → "東京都千代田区霞が関1234"
      // 例: "霞が関1234番地の5" → "霞が関1234-5"
      // 例: "霞が関1234番5号" → "霞が関1234-5"
      let normalized = input
        // 「番地の」→ ハイフン
        .replace(/(\d+)番地の(\d+)/g, '$1-$2')
        // 「番地」を除去
        .replace(/番地/g, '')
        // 「番」「号」→ ハイフン区切り
        .replace(/(\d+)番(\d+)号?/g, '$1-$2')
        // 単独の「番」を除去
        .replace(/(\d+)番$/g, '$1')
        // 「丁目」はそのまま（GSI APIが理解する）
        // 「字」は除去（場合によってはノイズになる）
        .replace(/大字/g, '')
        // 全角数字を半角に
        .replace(/[０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
      return normalized
    },

    async fetchSuggestions(query) {
      this.isLoading = true
      try {
        const formData = new FormData()
        formData.append('action', 'autocomplete')
        formData.append('input', query)
        // マップ中心でバイアス
        const map = this.$store.state.map01
        if (map) {
          const center = map.getCenter()
          formData.append('lat', String(center.lat))
          formData.append('lng', String(center.lng))
        }
        const response = await fetch('/oh3/php/placesSearchProxy.php', {
          method: 'POST',
          body: formData
        })
        const data = await response.json()
        this.suggestions = Array.isArray(data) ? data.slice(0, 8) : []
        this.showSuggestions = this.suggestions.length > 0
        this.selectedIndex = -1
        this.keyboardSelectedIndex = -1
      } catch (error) {
        console.warn('サジェスト取得エラー:', error)
        this.suggestions = []
        this.showSuggestions = false
      } finally {
        this.isLoading = false
      }
    },

    selectSuggestion(suggestion) {
      this.address = suggestion.properties.title
      this.closeSuggestions()
      this.goToLocation(suggestion)
    },

    closeSuggestions() {
      this.showSuggestions = false
      this.suggestions = []
      this.selectedIndex = -1
      this.keyboardSelectedIndex = -1
    },

    onBlur() {
      setTimeout(() => {
        this.closeSuggestions()
      }, 200)
    },

    onArrowDown() {
      if (!this.showSuggestions || this.suggestions.length === 0) return
      this.keyboardSelectedIndex = (this.keyboardSelectedIndex + 1) % this.suggestions.length
      this.selectedIndex = this.keyboardSelectedIndex // ハイライト同期
    },

    onArrowUp() {
      if (!this.showSuggestions || this.suggestions.length === 0) return
      this.keyboardSelectedIndex = this.keyboardSelectedIndex <= 0
        ? this.suggestions.length - 1
        : this.keyboardSelectedIndex - 1
      this.selectedIndex = this.keyboardSelectedIndex // ハイライト同期
    },

    goToLocation(suggestion) {
      const map = this.$store.state.map01
      const coordinates = suggestion.geometry.coordinates
      const lng = parseFloat(coordinates[0])
      const lat = parseFloat(coordinates[1])
      const title = suggestion.properties.title

      // 小画面時はFWを閉じる（flyToの前）
      this.closeWindowOnSmall()

      map.flyTo({
        center: [lng, lat],
        zoom: 14,
        essential: true
      })

      this.addMarkerWithPopup(map, lng, lat, title)
      // 住所検索成功: 1pt
      consumeToken('address_search')
      // 座標付きで履歴に保存
      this.recordSearchTermWithCoords(title, lat, lng)
    },

    // Enterキーハンドラ（IME変換中は無視）
    onEnterKey(event) {
      if (this.isComposing) {
        // IME変換中のEnterは無視
        return
      }
      event.preventDefault()
      this.sercheAdress()
    },

    async sercheAdress () {
      if (!this.address || !this.address.trim()) return;

      // Enterキーは入力文字列でGoogle検索
      // サジェストからの選択はクリックか↑↓+Enterで行う
      // ↑↓で明示的に選択した場合はそれを使う
      if (this.keyboardSelectedIndex >= 0 && this.suggestions[this.keyboardSelectedIndex]) {
        this.selectSuggestion(this.suggestions[this.keyboardSelectedIndex])
        this.keyboardSelectedIndex = -1
        return
      }

      // サジェストを閉じて、入力文字列で検索
      this.closeSuggestions()

      const map = this.$store.state.map01
      let input = this.address.trim();

      // 0. 「近くの〇〇」パターン検出 → Overpassモード
      const nearbyPattern = this.detectNearbyPattern(input)
      if (nearbyPattern) {
        await this.searchNearby(nearbyPattern)
        return
      }

      // 0.5 エリアPOI検索（例: 「宮崎市 ほっともっと」）
      if (this.looksLikeAreaPoi(input)) {
        const handled = await this.searchAreaPoi(input)
        if (handled) return
        // AIが「エリアPOI検索ではない」と判定 → 通常検索へフォールバック
      }

      // 1. 郵便番号形式（123-4567 または 1234567 または 〒880-0943 宮崎県...）- 座標より先にチェック
      if (this.isPostalCodeFormat(input)) {
        // 郵便番号部分だけを抽出（後ろに住所が続いていても対応）
        const postalCode = this.extractPostalCode(input)
        const address = await this.searchByPostalCode(postalCode)
        if (address) {
          // 郵便番号から取得した住所で検索
          // 履歴用に郵便番号をフォーマット（数字7桁を抽出してハイフン追加）
          const digits = postalCode.replace(/-/g, '')
          const formattedPostal = `${digits.slice(0,3)}-${digits.slice(3)}`
          this.searchParallel(address, `〒${formattedPostal} ${address}`)
          return
        } else {
          this.showError('郵便番号が見つかりませんでした')
          return
        }
      }

      // 2. 座標形式
      const coordRegex = /^\s*\[?\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\]?\s*$/;
      const match = coordRegex.exec(input);

      if (match) {
        let lat, lon;
        let val1 = parseFloat(match[1]);
        let val2 = parseFloat(match[2]);
        if (this.zahyokeiForSercheAdress === 'WGS84') {
          if (Math.abs(val1) <= 90 && Math.abs(val2) <= 180) {
            lat = val1;
            lon = val2;
          } else if (Math.abs(val2) <= 90 && Math.abs(val1) <= 180) {
            lat = val2;
            lon = val1;
          }
        } else {
          const zahyokeiMatch = this.zahyokeiForSercheAdress.match(/\d+/);
          const zone = zahyokeiMatch ? parseInt(zahyokeiMatch[0], 10) : null;
          const latLon  = jgd2000ZoneToWgs84(zone, val2, val1)
          lat = latLon.lat
          lon = latLon.lon
        }

        if (lat !== undefined && lon !== undefined) {
          // 小画面時はFWを閉じる
          this.closeWindowOnSmall()
          map.flyTo({ center: [lon, lat], zoom: 14 });
          this.addMarkerWithPopup(map, lon, lat, input)
          // 住所検索（座標入力）成功: 1pt
          consumeToken('address_search')
          this.recordSearchTerm(input)
          return;
        }
      }

      // 3. 地番形式の正規化（番地、番地の、番号などを含む場合）
      const chibanPattern = /番地|番\d|号/
      if (chibanPattern.test(input)) {
        input = this.normalizeChiban(input)
      }

      // 4. Google Places Text Searchで検索
      this.searchParallel(input, this.address.trim())
    },

    // Google Places Text Searchで検索し、結果をダイアログで表示
    async searchParallel(searchQuery, recordTerm) {
      this.searchRecordTerm = recordTerm
      this.isSearching = true
      let results = []

      try {
        const formData = new FormData()
        formData.append('action', 'textsearch')
        formData.append('query', searchQuery)
        const response = await fetch('/oh3/php/placesSearchProxy.php', {
          method: 'POST',
          body: formData
        })
        const data = await response.json()
        if (Array.isArray(data)) {
          // タイトル重複を除去
          const seen = new Set()
          results = data.filter(r => {
            const title = r.properties?.title || ''
            if (seen.has(title)) return false
            seen.add(title)
            return true
          }).map(r => ({ ...r, source: 'google' }))
        }
      } catch (e) {
        console.warn('Google検索エラー:', e)
      } finally {
        this.isSearching = false
      }

      if (results.length === 0) {
        this.showError('住所が見つかりませんでした。別の住所を入力してください。')
        return
      }

      if (results.length === 1) {
        this.goToSearchResult(results[0], recordTerm)
        return
      }

      // 複数件なら選択ダイアログを表示
      this.searchResults = results
      this.showResultsDialog = true
    },

    // 「近くの〇〇」パターンを検出
    // 戻り値: { facilityType: string, originalQuery: string } または null
    detectNearbyPattern(query) {
      // 「近くの」「付近の」「周辺の」などのパターン
      const patterns = [
        /近くの(.+)/,
        /近い(.+)/,
        /(.+)が近い/,
        /(.+)が近く/,
        /付近の(.+)/,
        /(.+)の付近/,
        /周辺の(.+)/,
        /(.+)の周辺/,
        /最寄りの(.+)/,
        /一番近い(.+)/
      ]

      for (const pattern of patterns) {
        const match = query.match(pattern)
        if (match) {
          const facilityType = match[1].trim()
          return {
            facilityType,
            originalQuery: query
          }
        }
      }
      return null
    },

    // 「近くの〇〇」検索処理（ステップ②③をログ出力）
    async searchNearby(nearbyPattern) {
      this.isSearching = true
      try {
        // ② AIでOverpassタグを抽出
        const osmTags = await this.extractOsmTagsViaAi(nearbyPattern.facilityType)

        // ③ 現在地を取得
        const currentLocation = await this.getCurrentLocation()

        // タグ抽出失敗時は終了
        if (!osmTags.success) {
          this.showError('施設タイプを特定できませんでした')
          return
        }

        // ④ Overpass APIで周辺施設を検索
        const overpassResult = await this.executeOverpassNearby(osmTags.query, currentLocation)

        if (!overpassResult.success || overpassResult.features.length === 0) {
          this.showError('周辺に該当する施設が見つかりませんでした')
          return
        }

        // ⑤ 距離計算してソート、上位5件をリスト表示
        const sortedFeatures = this.sortByDistance(overpassResult.features, currentLocation)
        const top5 = sortedFeatures.slice(0, 5)

        // 既存のsearchResults形式に変換してダイアログ表示
        const results = top5.map(f => ({
          geometry: {
            type: 'Point',
            coordinates: [f.lng, f.lat]
          },
          properties: {
            title: `${f.name}（${f.distanceText}）`
          },
          source: 'nearby'
        }))

        if (results.length === 1) {
          // 1件のみなら直接移動（施設名を履歴に記録）
          const facilityName = top5[0].name
          this.goToSearchResult(results[0], facilityName)
        } else {
          // 複数件ならリスト表示（選択時に施設名を記録）
          this.searchResults = results
          this.showResultsDialog = true
        }
      } catch (e) {
        console.error('近くの検索エラー:', e)
        this.showError('検索中にエラーが発生しました: ' + e.message)
      } finally {
        this.isSearching = false
      }
    },

    // === エリアPOI検索 ===

    // 事前フィルタ: エリア名+何かのパターンっぽいか
    looksLikeAreaPoi(input) {
      return /(.+[市町村区県都道府])\s+(.+)/.test(input)
    },

    // エリアPOI検索のメインフロー（handled=trueを返したら呼び出し元はreturn）
    async searchAreaPoi(input) {
      this.isSearching = true
      try {
        // ① AIにエリアPOI判定 + Overpass QL生成を依頼
        const response = await axios.post(
          '/oh3/php/claudeAreaPoiQuery.php',
          { userQuery: input },
          { timeout: 15000 }
        )

        if (!response.data?.success || response.data.type !== 'area_poi') {
          return false // エリアPOI検索ではない → フォールバック
        }

        const { areaName, poiQuery, query } = response.data

        // ② Overpass API実行
        const overpassResult = await this.executeOverpassArea(query)

        if (!overpassResult.success || overpassResult.features.length === 0) {
          this.showError(`${areaName}で「${poiQuery}」が見つかりませんでした`)
          return true // handled（結果0件だがエリアPOI検索自体は処理済み）
        }


        // ③ 一時マーカーを地図に表示 + fitBounds
        this.showAreaPoiMarkers(overpassResult.features)

        // ④ 結果をダイアログに表示
        this.searchRecordTerm = input
        this.areaPoiInfo = { areaName, poiQuery, count: overpassResult.features.length }
        this.searchResults = overpassResult.features.map(f => ({
          geometry: {
            type: 'Point',
            coordinates: [f.lng, f.lat]
          },
          properties: {
            title: f.name + (f.tags?.branch ? ` ${f.tags.branch}` : '') + (f.tags?.['addr:full'] ? `（${f.tags['addr:full']}）` : '')
          },
          source: 'area_poi'
        }))

        if (this.searchResults.length === 1) {
          this.goToSearchResult(this.searchResults[0], this.searchResults[0].properties.title)
        } else {
          this.showResultsDialog = true
        }

        consumeToken('address_search')
        return true
      } catch (e) {
        console.error('エリアPOI検索エラー:', e)
        return false // エラー時はフォールバック
      } finally {
        this.isSearching = false
      }
    },

    // Overpass API実行（area構文、bbox置換なし、フォールバック付き）
    async executeOverpassArea(query) {
      const servers = [
        'https://overpass.kumi.systems/api/interpreter',
        'https://overpass-api.de/api/interpreter',
      ]

      for (const url of servers) {
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: `data=${encodeURIComponent(query)}`
          })

          if (!response.ok) {
            console.warn(`Overpass ${url} → ${response.status}、次のサーバーへ`)
            continue
          }

          const data = await response.json()
          return { success: true, features: this.parseOverpassElements(data) }
        } catch (e) {
          console.warn(`Overpass ${url} → エラー:`, e.message)
          continue
        }
      }

      console.error('Overpass(area): 全サーバー失敗')
      return { success: false, features: [], error: '全サーバー応答なし' }
    },

    // Overpass APIレスポンスからfeature配列を抽出（共通化）
    parseOverpassElements(data) {
      const features = []
      if (!data.elements) return features

      data.elements.forEach(el => {
        if (!el.tags || Object.keys(el.tags).length === 0) return

        const name = el.tags.name ||
                     el.tags['name:ja'] ||
                     el.tags.operator ||
                     el.tags.brand ||
                     null

        if (!name) return

        if (el.type === 'node' && el.lat && el.lon) {
          features.push({ type: 'node', id: el.id, lat: el.lat, lng: el.lon, name, tags: el.tags })
        } else if (el.type === 'way' && el.center) {
          features.push({ type: 'way', id: el.id, lat: el.center.lat, lng: el.center.lon, name, tags: el.tags })
        }
      })

      return features
    },

    // エリアPOI結果を一時マーカーとして地図に表示
    showAreaPoiMarkers(features) {
      const map = this.$store.state.map01
      if (!map) return

      // 既存の一時マーカーをクリア
      this.clearAreaPoiMarkers()

      // GeoJSON FeatureCollectionを作成
      const fc = {
        type: 'FeatureCollection',
        features: features.map(f => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [f.lng, f.lat] },
          properties: { name: f.name }
        }))
      }

      // ソース・レイヤー追加
      const sourceId = 'area-poi-temp-source'
      const circleLayerId = 'area-poi-temp-circle'
      const labelLayerId = 'area-poi-temp-label'

      if (map.getSource(sourceId)) {
        map.getSource(sourceId).setData(fc)
      } else {
        map.addSource(sourceId, { type: 'geojson', data: fc })
      }

      if (!map.getLayer(circleLayerId)) {
        map.addLayer({
          id: circleLayerId,
          type: 'circle',
          source: sourceId,
          paint: {
            'circle-radius': 7,
            'circle-color': '#e65100',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
          }
        })
      }

      if (!map.getLayer(labelLayerId)) {
        map.addLayer({
          id: labelLayerId,
          type: 'symbol',
          source: sourceId,
          layout: {
            'text-field': ['get', 'name'],
            'text-size': 12,
            'text-offset': [0, 1.5],
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': '#333',
            'text-halo-color': '#fff',
            'text-halo-width': 1.5,
          }
        })
      }

      // fitBoundsで全件表示
      if (features.length > 0) {
        let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity
        features.forEach(f => {
          if (f.lng < minLng) minLng = f.lng
          if (f.lat < minLat) minLat = f.lat
          if (f.lng > maxLng) maxLng = f.lng
          if (f.lat > maxLat) maxLat = f.lat
        })
        map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 60, maxZoom: 14 })
      }
    },

    // 一時マーカーをクリア
    clearAreaPoiMarkers() {
      const map = this.$store.state.map01
      if (!map) return
      const sourceId = 'area-poi-temp-source'
      if (map.getLayer('area-poi-temp-label')) map.removeLayer('area-poi-temp-label')
      if (map.getLayer('area-poi-temp-circle')) map.removeLayer('area-poi-temp-circle')
      if (map.getSource(sourceId)) map.removeSource(sourceId)
    },

    // AIでOSMタグを抽出（claudeOsmQuery.phpと同様のアプローチ）
    async extractOsmTagsViaAi(facilityType) {
      try {
        const response = await axios.post(
          '/oh3/php/claudeOsmQuery.php',
          { userQuery: facilityType },
          { timeout: 10000 }
        )

        if (response.data?.success && response.data.query) {
          return {
            success: true,
            query: response.data.query
          }
        }
        return { success: false, error: 'クエリ生成失敗' }
      } catch (e) {
        console.warn('OSMタグ抽出エラー:', e.message)
        return { success: false, error: e.message }
      }
    },

    // 現在地を取得
    getCurrentLocation() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          // Geolocation非対応の場合は地図の中心を使用
          const map = this.$store.state.map01
          const center = map.getCenter()
          resolve({
            lat: center.lat,
            lng: center.lng,
            source: 'map-center'
          })
          return
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              source: 'gps'
            })
          },
          (error) => {
            // GPS取得失敗時は地図の中心を使用
            console.warn('GPS取得失敗、地図中心を使用:', error.message)
            const map = this.$store.state.map01
            const center = map.getCenter()
            resolve({
              lat: center.lat,
              lng: center.lng,
              source: 'map-center'
            })
          },
          { timeout: 5000, enableHighAccuracy: false }
        )
      })
    },

    // Overpass APIで周辺施設を検索
    async executeOverpassNearby(queryTemplate, location) {
      try {
        // 現在地を中心に約2km四方のbboxを作成
        const radius = 0.02 // 約2km（緯度経度の差）
        const bbox = `${location.lat - radius},${location.lng - radius},${location.lat + radius},${location.lng + radius}`

        // {{bbox}}を実際の値に置換
        let query = queryTemplate.replace(/\{\{bbox\}\}/g, bbox)

        // wayのcenter座標を取得するため、out bodyをout center bodyに変更
        // out skel qt; は不要なので削除し、out center body qt; に統一
        query = query.replace(/out body;\s*>;\s*out skel qt;/g, 'out center body qt;')
        query = query.replace(/out body;/g, 'out center body qt;')


        // フォールバック付きOverpass API実行
        const servers = [
          'https://overpass.kumi.systems/api/interpreter',
          'https://overpass-api.de/api/interpreter',
        ]

        let data = null
        for (const url of servers) {
          try {
            const response = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body: `data=${encodeURIComponent(query)}`
            })
            if (!response.ok) {
              console.warn(`Overpass ${url} → ${response.status}、次のサーバーへ`)
              continue
            }
            data = await response.json()
            break
          } catch (e) {
            console.warn(`Overpass ${url} → ${e.message}、次のサーバーへ`)
            continue
          }
        }

        if (!data) {
          throw new Error('すべてのOverpassサーバーに接続できませんでした')
        }

        // OSMデータから座標と名前を抽出
        const features = []

        // elementsを処理（tagsがある=施設データのみ抽出）
        if (data.elements) {
          data.elements.forEach(el => {
            // tagsが空のノードは除外（wayの構成点など）
            if (!el.tags || Object.keys(el.tags).length === 0) return

            // 名前を取得（複数の候補から探す）
            const name = el.tags.name ||
                         el.tags['name:ja'] ||
                         el.tags.operator ||
                         el.tags.brand ||
                         null

            // 名前がない施設は除外
            if (!name) return

            if (el.type === 'node' && el.lat && el.lon) {
              features.push({
                type: 'node',
                id: el.id,
                lat: el.lat,
                lng: el.lon,
                name,
                tags: el.tags
              })
            } else if (el.type === 'way' && el.center) {
              features.push({
                type: 'way',
                id: el.id,
                lat: el.center.lat,
                lng: el.center.lon,
                name,
                tags: el.tags
              })
            }
          })
        }

        return { success: true, features }
      } catch (e) {
        console.error('Overpass実行エラー:', e)
        return { success: false, features: [], error: e.message }
      }
    },

    // 距離でソート（Haversine公式）
    sortByDistance(features, location) {
      const toRad = deg => deg * Math.PI / 180
      const R = 6371 // 地球の半径（km）

      return features.map(f => {
        const dLat = toRad(f.lat - location.lat)
        const dLng = toRad(f.lng - location.lng)
        const a = Math.sin(dLat / 2) ** 2 +
                  Math.cos(toRad(location.lat)) * Math.cos(toRad(f.lat)) *
                  Math.sin(dLng / 2) ** 2
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
        const distance = R * c // km

        return {
          ...f,
          distance,
          distanceText: distance < 1
            ? `${Math.round(distance * 1000)}m`
            : `${distance.toFixed(1)}km`
        }
      }).sort((a, b) => a.distance - b.distance)
    },

    // 検索結果から地図へ移動
    goToSearchResult(result, recordTerm) {
      const map = this.$store.state.map01
      const coordinates = result.geometry.coordinates
      const lng = parseFloat(coordinates[0])
      const lat = parseFloat(coordinates[1])
      const title = result.properties?.title || recordTerm

      // 296デバッグ: 移動先ログ
      if (title.includes('296')) {
      }

      // 小画面時はFWを閉じる
      this.closeWindowOnSmall()

      map.flyTo({
        center: [lng, lat],
        zoom: 14,
        essential: true
      })

      this.addMarkerWithPopup(map, lng, lat, title)
      // 住所検索成功: 1pt
      consumeToken('address_search')
      // ユーザー入力語と座標を履歴に保存
      this.recordSearchTermWithCoords(recordTerm, lat, lng)
    },

    // 検索結果ダイアログで選択
    selectSearchResult(result) {
      this.showResultsDialog = false
      // nearbyソースの場合は施設名を履歴に記録（距離部分を除く）
      let recordTerm = this.searchRecordTerm
      if (result.source === 'nearby') {
        // 「施設名（距離）」から施設名だけを抽出
        const title = result.properties?.title || ''
        recordTerm = title.replace(/（[^）]+）$/, '').trim() || title
      }
      this.goToSearchResult(result, recordTerm)
    },

    // 結果ダイアログを閉じる
    closeResultsDialog() {
      this.showResultsDialog = false
      this.areaPoiInfo = null
      // エリアPOI一時マーカーをクリア
      this.clearAreaPoiMarkers()
    },

    // ソースバッジのクラス
    getSourceClass(source) {
      if (source === 'google') return 'source-google'
      if (source === 'nearby') return 'source-nearby'
      if (source === 'area_poi') return 'source-area-poi'
      return 'source-google'
    },

    // ソースバッジのラベル
    getSourceLabel(source) {
      if (source === 'google') return 'Google'
      if (source === 'nearby') return '近く'
      if (source === 'area_poi') return 'エリア'
      return 'Google'
    },

    // 履歴のterm取得（オブジェクト/文字列両対応）
    getHistoryTerm(item) {
      return typeof item === 'string' ? item : item.term
    },

    // 履歴に座標付きで保存
    recordSearchTermWithCoords(term, lat, lng) {
      if (!this.isLoggedIn) return
      this.$store.dispatch('addSearchHistory', { term, lat, lng })
    },

    removeHistory (term) {
      if (!this.isLoggedIn) return
      this.$store.dispatch('removeSearchHistory', term)
    },

    // 履歴選択時：座標があれば再検索せずに直接移動
    selectHistory (item) {
      const term = this.getHistoryTerm(item)
      this.address = term

      // 座標が保存されていればそのまま使用
      if (typeof item === 'object' && item.lat && item.lng) {
        const map = this.$store.state.map01
        this.closeWindowOnSmall()
        map.flyTo({
          center: [item.lng, item.lat],
          zoom: 14,
          essential: true
        })
        this.addMarkerWithPopup(map, item.lng, item.lat, term)
        // 使った履歴を先頭に移動
        this.recordSearchTermWithCoords(term, item.lat, item.lng)
        return
      }

      // 座標がない古い履歴は再検索
      this.sercheAdress()
    },

    // お気に入り機能
    addCurrentLocationToFavorite() {
      const map = this.$store.state.map01
      const center = map.getCenter()
      const zoom = map.getZoom()

      this.favoriteCoords = {
        lat: center.lat,
        lng: center.lng,
        zoom: Math.round(zoom)
      }
      this.favoriteName = ''
      this.editingFavorite = null
      this.showFavoriteDialog = true
    },

    editFavorite(fav) {
      this.favoriteCoords = {
        lat: fav.lat,
        lng: fav.lng,
        zoom: fav.zoom || 14
      }
      this.favoriteName = fav.name
      this.editingFavorite = fav.name
      this.showFavoriteDialog = true
    },

    closeFavoriteDialog() {
      this.showFavoriteDialog = false
      this.favoriteName = ''
      this.editingFavorite = null
    },

    // IME変換中のEnterを無視
    onFavoriteEnter(e) {
      if (e.isComposing) return
      this.saveFavorite()
    },

    async saveFavorite() {
      const name = this.favoriteName.trim()
      if (!name) return

      const location = {
        name,
        lat: this.favoriteCoords.lat,
        lng: this.favoriteCoords.lng,
        zoom: this.favoriteCoords.zoom,
        createdAt: new Date().toISOString()
      }

      if (this.editingFavorite && this.editingFavorite !== name) {
        await this.$store.dispatch('updateFavoriteLocation', {
          oldName: this.editingFavorite,
          newLocation: location
        })
      } else {
        await this.$store.dispatch('addFavoriteLocation', location)
      }

      this.closeFavoriteDialog()
    },

    async removeFavorite(name) {
      const confirmed = await this.showConfirm({
        title: '削除確認',
        message: `「${name}」をお気に入りから削除しますか？`,
        color: 'error',
        icon: 'mdi-delete',
        iconColor: 'error',
      })
      if (confirmed) {
        await this.$store.dispatch('removeFavoriteLocation', name)
      }
    },

    // 確認ダイアログをプロミス化
    showConfirm(options = {}) {
      return new Promise((resolve) => {
        this.confirmDialog = {
          show: true,
          title: options.title || '確認',
          message: options.message || '',
          color: options.color || 'primary',
          icon: options.icon || null,
          iconColor: options.iconColor || 'primary',
          resolve: (result) => {
            this.confirmDialog.show = false
            resolve(result)
          },
        }
      })
    },

    goToFavorite(fav) {
      const map = this.$store.state.map01

      // 小画面時はFWを閉じる
      this.closeWindowOnSmall()

      map.flyTo({
        center: [fav.lng, fav.lat],
        zoom: fav.zoom || 14,
        essential: true
      })

      this.addMarkerWithPopup(map, fav.lng, fav.lat, fav.name)
    },

    // ポップアップ付きマーカーを追加
    async addMarkerWithPopup(map, lng, lat, title) {
      // 既存のマーカーと近すぎる場合はスキップ（約10m以内）
      const MIN_DISTANCE = 0.0001; // 約10m
      const tooClose = this.markers.some(m => {
        const dLng = Math.abs(lng - m.lngLat[0]);
        const dLat = Math.abs(lat - m.lngLat[1]);
        return dLng < MIN_DISTANCE && dLat < MIN_DISTANCE;
      });
      if (tooClose) {
        return;
      }

      // サーバーに保存してIDを取得
      const savedMarker = await this.$store.dispatch('addSavedMarker', {
        lng: lng,
        lat: lat,
        title: title,
        marker_type: 'address_search'
      });
      const markerId = savedMarker?.id || null;

      const popupContent = this.createPopupContent(lng, lat, title, markerId)

      const popup = new maplibregl.Popup({
        offset: 25,
        closeButton: true,
        closeOnClick: false,
        maxWidth: '280px'
      }).setHTML(popupContent)

      const marker = new maplibregl.Marker({ color: '#1976D2' })
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(map)

      // マーカーを配列に追加
      const markerInfo = { marker, lngLat: [lng, lat], markerId }
      this.markers.push(markerInfo)

      // ポップアップ内のボタンにイベントを設定
      popup.on('open', () => {
        this.setupPopupButtons(popup, marker, lng, lat, markerId, markerInfo)
      })

      // マーカークリックでポップアップを開く
      marker.getElement().style.cursor = 'pointer'
    },

    // ポップアップのHTMLを生成（シンプル化：コピー＆削除のみ）
    createPopupContent(lng, lat, title, markerId = null) {
      return `
        <div class="marker-popup" data-marker-id="${markerId || ''}">
          <div class="popup-title">${title || '検索地点'}</div>
          <div class="popup-actions">
            <button class="popup-btn popup-btn-copy" data-lng="${lng}" data-lat="${lat}" data-title="${title || '検索地点'}" title="地点情報をコピー">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
              </svg>
              コピー
            </button>
            <button class="popup-btn popup-btn-delete" title="マーカーを削除">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
              削除
            </button>
          </div>
        </div>
      `
    },

    // ポップアップ内のボタンにイベントリスナーを設定
    setupPopupButtons(popup, marker, lng, lat, markerId = null, markerInfo = null) {
      const popupEl = popup.getElement()
      if (!popupEl) return

      // コピーボタン（詳細情報をコピー）
      const copyBtn = popupEl.querySelector('.popup-btn-copy')
      if (copyBtn) {
        copyBtn.addEventListener('click', async (e) => {
          e.stopPropagation()
          const btnLng = parseFloat(copyBtn.dataset.lng)
          const btnLat = parseFloat(copyBtn.dataset.lat)
          await this.copyLocationInfo(btnLng, btnLat, copyBtn)
        })
      }

      // 削除ボタン
      const deleteBtn = popupEl.querySelector('.popup-btn-delete')
      if (deleteBtn) {
        deleteBtn.addEventListener('click', (e) => {
          e.stopPropagation()
          marker.remove()
          // 配列からも削除
          if (markerInfo) {
            const idx = this.markers.indexOf(markerInfo)
            if (idx !== -1) this.markers.splice(idx, 1)
          }
          // サーバーからも削除
          if (markerId) {
            this.$store.dispatch('removeSavedMarker', markerId)
          }
        })
      }
    },

    // 地点情報を取得してクリップボードにコピー
    async copyLocationInfo(lng, lat, btn) {
      // ボタンをローディング状態に
      const originalText = btn.innerHTML
      btn.innerHTML = `
        <svg class="spinner" width="16" height="16" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-dasharray="31.4" stroke-dashoffset="10">
            <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
          </circle>
        </svg>
      `
      btn.disabled = true

      try {
        // 並列で情報を取得
        const [elevation, address] = await Promise.all([
          fetchElevation(lng, lat).catch(() => null),
          LngLatToAddress(lng, lat).catch(() => null)
        ])

        // 座標系を取得（storeから）
        const zahyokeiLabel = this.$store.state.zahyokei || ''
        const zahyokeiItem = zahyokeiList.find(item => item.kei === zahyokeiLabel)

        // JDP座標を計算
        let jdpCoords = ''
        if (zahyokeiItem) {
          try {
            const jdp = proj4('EPSG:4326', zahyokeiItem.code, [lng, lat])
            jdpCoords = `${jdp[1].toFixed(3)}, ${jdp[0].toFixed(3)}`
          } catch (e) {
            console.warn('JDP座標変換エラー:', e)
          }
        }

        // WGS84座標
        const wgs84Coords = `${lng.toFixed(6)}, ${lat.toFixed(6)}`

        // コピーテキストを組み立て（ZoomInfoWindowと同じ形式）
        const items = []
        if (elevation !== null && elevation !== undefined && elevation <= 4000) {
          items.push(`標高: 中心の標高: ${elevation.toFixed(2)}m`)
        }
        if (address) {
          items.push(`住所: ${address}`)
        }
        if (zahyokeiLabel) {
          items.push(`座標系: ${zahyokeiLabel}`)
        }
        if (jdpCoords) {
          items.push(`JDP座標: ${jdpCoords}`)
        }
        items.push(`WGS84座標: ${wgs84Coords}`)

        const copyText = items.join('\n')

        // クリップボードにコピー
        await navigator.clipboard.writeText(copyText)

        // 成功フィードバック
        btn.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
          完了
        `
        btn.style.backgroundColor = '#4caf50'
        btn.style.color = 'white'

        setTimeout(() => {
          btn.innerHTML = originalText
          btn.style.backgroundColor = ''
          btn.style.color = ''
          btn.disabled = false
        }, 1500)

      } catch (err) {
        console.error('地点情報の取得またはコピーに失敗:', err)
        // フォールバック: 座標だけコピー
        const fallbackText = `WGS84座標: ${lng.toFixed(6)}, ${lat.toFixed(6)}`
        try {
          await navigator.clipboard.writeText(fallbackText)
        } catch (e) {
          // 古いブラウザ用フォールバック
          const textArea = document.createElement('textarea')
          textArea.value = fallbackText
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
        }

        btn.innerHTML = originalText
        btn.disabled = false
      }
    },

    // エラーダイアログ表示
    showError(message) {
      this.errorMessage = message
      this.showErrorDialog = true
    },

    // 保存済みマーカーを復元
    restoreSavedMarkers() {
      const map = this.map01
      if (!map) {
        // マップが未準備の場合は少し待ってリトライ
        setTimeout(() => this.restoreSavedMarkers(), 500)
        return
      }

      const savedMarkers = this.$store.state.savedMarkers || []
      savedMarkers.forEach(markerData => {
        this.restoreMarker(map, markerData)
      })
    },

    // 個別のマーカーを復元
    restoreMarker(map, markerData) {
      const { id, lng, lat, title } = markerData
      const popupContent = this.createPopupContent(lng, lat, title, id)

      const popup = new maplibregl.Popup({
        offset: 25,
        closeButton: true,
        closeOnClick: false,
        maxWidth: '280px'
      }).setHTML(popupContent)

      const marker = new maplibregl.Marker({ color: '#1976D2' })
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(map)

      // マーカーを配列に追加
      const markerInfo = { marker, lngLat: [lng, lat], markerId: id }
      this.markers.push(markerInfo)

      popup.on('open', () => {
        this.setupPopupButtons(popup, marker, lng, lat, id, markerInfo)
      })

      marker.getElement().style.cursor = 'pointer'
    }
  },
}
</script>

<style scoped>
.address-search-container {
  display: flex;
  flex-direction: column;
  background-color: var(--oh3-color-gray-50);
  height: 100%;
  overflow: hidden; /* 親コンテナはスクロールしない */
}

/* 固定ヘッダー部分 */
.search-header {
  flex-shrink: 0;
  padding: 16px 16px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tabs-header {
  margin-top: var(--oh3-spacing-2);
  flex-shrink: 0;
}

/* スクロール可能なコンテンツ */
.scroll-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0; /* flexboxでスクロールさせるために必要 */
}

/* AI検索ヒント */
.ai-hint {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  margin-top: var(--oh3-spacing-1);
  font-size: 11px;
  color: var(--oh3-color-text-secondary);
  background: var(--oh3-color-gray-100);
  border-radius: var(--oh3-radius-base);
}

/* 座標系オプション（折りたたみ） */
.coord-options {
  margin-top: var(--oh3-spacing-2);
}

.coord-toggle {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  font-size: var(--oh3-font-size-sm);
  color: var(--oh3-color-text-secondary);
  cursor: pointer;
  border-radius: var(--oh3-radius-base);
  transition: background-color 0.15s;
}

.coord-toggle:hover {
  background-color: #f0f0f0;
}

.coord-badge {
  margin-left: auto;
  padding: 2px 6px;
  background: #e3f2fd;
  color: var(--oh3-color-link);
  border-radius: var(--oh3-radius-base);
  font-size: 11px;
  font-weight: 500;
}

.marker-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-top: 1px solid #e0e0e0;
}

.marker-count {
  display: flex;
  align-items: center;
  font-size: var(--oh3-font-size-sm, 12px);
  color: #666;
  margin-right: auto;
}

.coord-select-wrapper {
  padding: 8px 8px 0;
}

.native-select {
  padding: 10px 12px;
  font-size: 1rem;
  border: 1px solid var(--oh3-color-overlay-20);
  border-radius: var(--oh3-radius-lg);
  background-color: var(--oh3-color-white);
  width: 100%;
}

.native-select-small {
  padding: 8px 10px;
  font-size: 0.9rem;
}

.native-select:disabled {
  opacity: 0.6;
}

.search-field {
  margin-top: 0;
}

/* オートコンプリート */
.autocomplete-wrapper {
  position: relative;
}

.suggestions-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid var(--oh3-color-border-default);
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 12px var(--oh3-color-overlay-12);
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;
}

.suggestion-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  font-size: var(--oh3-font-size-base);
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.15s;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover,
.suggestion-item--active {
  background-color: #e3f2fd;
}

.suggestions-loading {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  font-size: 13px;
  color: var(--oh3-color-text-secondary);
  background: white;
  border: 1px solid var(--oh3-color-border-default);
  border-top: none;
  border-radius: 0 0 8px 8px;
}

.tab-panel {
  padding: var(--oh3-spacing-4);
}

.panel-header {
  margin-bottom: var(--oh3-spacing-3);
}

.add-favorite-btn {
  width: 100%;
}

/* お気に入りリスト */
.favorite-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.favorite-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: var(--oh3-radius-lg);
  cursor: pointer;
  transition: all 0.2s ease;
}

.favorite-item:hover {
  background: #e3f2fd;
  transform: translateX(2px);
}

.favorite-info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.favorite-icon {
  color: #9e9e9e;
  margin-right: var(--oh3-spacing-2);
}

.favorite-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--oh3-color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.favorite-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: var(--oh3-spacing-2);
  opacity: 0.6;
  transition: opacity 0.2s;
}

.favorite-item:hover .favorite-actions {
  opacity: 1;
}

/* 履歴リスト */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.history-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: var(--oh3-radius-lg);
  cursor: pointer;
  transition: all 0.2s ease;
}

.history-item:hover {
  background: #e3f2fd;
  transform: translateX(2px);
}

.history-icon {
  color: #9e9e9e;
  margin-right: 10px;
}

.history-text {
  flex: 1;
  font-size: 13px;
  color: var(--oh3-color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* アクションボタン */
.action-btn {
  color: var(--oh3-color-text-tertiary);
  transition: all 0.2s;
}

.action-btn:hover {
  color: var(--oh3-color-text-secondary);
}

.action-btn--delete:hover {
  color: #e53935;
}

/* 空状態 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
}

.empty-state p {
  margin: 12px 0 4px;
  font-size: var(--oh3-font-size-base);
  font-weight: 500;
  color: var(--oh3-color-text-secondary);
}

.empty-hint {
  font-size: var(--oh3-font-size-sm);
  color: var(--oh3-color-text-tertiary);
}

/* ログインプロンプト */
.login-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--oh3-spacing-4);
  background: var(--oh3-color-gray-100);
  border-radius: var(--oh3-radius-lg);
  font-size: 13px;
  color: var(--oh3-color-text-secondary);
}

/* ダイアログ */
.favorite-dialog {
  border-radius: var(--oh3-radius-xl);
}

.dialog-title {
  display: flex;
  align-items: center;
  font-size: var(--oh3-font-size-md);
  font-weight: 600;
  padding-bottom: var(--oh3-spacing-2);
}

.coords-display {
  display: flex;
  align-items: center;
  margin-top: var(--oh3-spacing-3);
  padding: 8px 12px;
  background: var(--oh3-color-gray-100);
  border-radius: var(--oh3-radius-md);
  font-size: var(--oh3-font-size-sm);
  color: var(--oh3-color-text-secondary);
  font-family: monospace;
}

.zoom-badge {
  margin-left: auto;
  background: var(--oh3-color-gray-300);
  padding: 2px 6px;
  border-radius: var(--oh3-radius-base);
  font-size: 11px;
  font-weight: 600;
}

/* エラーダイアログ */
.error-dialog {
  border-radius: var(--oh3-radius-2xl);
  overflow: hidden;
}

.error-content {
  padding: 24px 24px 16px;
  text-align: center;
}

.error-icon-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: var(--oh3-spacing-4);
}

.error-message {
  font-size: 15px;
  color: var(--oh3-color-text-primary);
  line-height: 1.5;
  margin: 0;
}

.error-actions {
  padding: 8px 16px 16px;
}

/* 検索結果ダイアログ */
.results-dialog {
  border-radius: var(--oh3-radius-2xl);
  overflow: hidden;
}

.results-title {
  display: flex;
  align-items: center;
  font-size: var(--oh3-font-size-md);
  font-weight: 600;
  padding: 16px 20px 8px;
}

.results-content {
  padding: 0 8px 8px;
  max-height: 400px;
  overflow-y: auto;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.result-item {
  display: flex;
  align-items: center;
  padding: var(--oh3-spacing-3);
  background: #f8f9fa;
  border-radius: var(--oh3-radius-lg);
  cursor: pointer;
  transition: all 0.2s ease;
}

.result-item:hover {
  background: #e3f2fd;
  transform: translateX(2px);
}

.result-rank {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--oh3-color-link);
  color: var(--oh3-color-white);
  border-radius: var(--oh3-radius-full);
  font-size: var(--oh3-font-size-sm);
  font-weight: 600;
  margin-right: var(--oh3-spacing-3);
  flex-shrink: 0;
}

.result-source {
  padding: 2px 8px;
  border-radius: var(--oh3-radius-base);
  font-size: 11px;
  font-weight: 600;
  margin-right: 10px;
  flex-shrink: 0;
}

.source-google {
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
}

.source-nearby {
  background: #fce4ec;
  color: #c62828;
  border: 1px solid #ef9a9a;
}

.source-area-poi {
  background: #fff3e0;
  color: #e65100;
  border: 1px solid #ffcc80;
}

.result-info {
  flex: 1;
  min-width: 0;
}

.result-title {
  font-size: var(--oh3-font-size-base);
  color: var(--oh3-color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* レスポンシブ調整 */
@media (max-width: 500px) {
  .address-search-container {
    padding: var(--oh3-spacing-3);
  }

  .tab-button {
    padding: 8px 10px;
    font-size: var(--oh3-font-size-sm);
  }

  .tab-badge {
    display: none;
  }
}
</style>

<!-- ポップアップスタイル（MapLibreのポップアップはVueコンポーネント外に生成されるため非scoped） -->
<style>
.marker-popup {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  padding: 10px;
}

.popup-title {
  font-size: var(--oh3-font-size-base);
  font-weight: 600;
  color: var(--oh3-color-text-primary);
  margin-bottom: 10px;
  padding-bottom: var(--oh3-spacing-2);
  border: 1px solid var(--oh3-color-border-default);
  word-break: break-all;
}

.popup-actions {
  display: flex;
  gap: 8px;
}

.popup-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 16px;
  border: none;
  border-radius: var(--oh3-radius-md);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
}

.popup-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.popup-btn-copy {
  background: #e3f2fd;
  color: var(--oh3-color-link);
}

.popup-btn-copy:hover:not(:disabled) {
  background: #bbdefb;
}

.popup-btn-delete {
  background: #ffebee;
  color: #d32f2f;
}

.popup-btn-delete:hover {
  background: #ffcdd2;
}

/* MapLibreポップアップのデフォルトスタイル上書き */
.maplibregl-popup-content {
  padding: var(--oh3-spacing-3);
  border-radius: var(--oh3-radius-lg);
  box-shadow: 0 2px 12px var(--oh3-color-overlay-15);
  min-width: 210px;
}

.maplibregl-popup-close-button {
  font-size: var(--oh3-font-size-lg);
  padding: 4px 8px;
  color: var(--oh3-color-text-secondary);
}

.maplibregl-popup-close-button:hover {
  background: var(--oh3-color-gray-100);
  color: var(--oh3-color-text-primary);
}
</style>
